import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  fetchWeatherData,
  requestGeolocation,
  DEFAULT_LOCATION,
  WEATHER_CACHE_TTL,
  type WeatherData,
  type GeoLocation,
} from '@/services/weatherService'

interface WeatherState {
  weatherData: WeatherData | null
  location: GeoLocation | null
  lastFetchedAt: number | null
  isLoading: boolean
  error: string | null
  locationDenied: boolean

  fetchWeather: () => Promise<void>
  resolveLocation: () => Promise<void>
  isStale: () => boolean
}

export const useWeatherStore = create<WeatherState>()(
  persist(
    (set, get) => ({
      weatherData: null,
      location: null,
      lastFetchedAt: null,
      isLoading: false,
      error: null,
      locationDenied: false,

      isStale: () => {
        const { lastFetchedAt } = get()
        if (!lastFetchedAt) return true
        return Date.now() - lastFetchedAt > WEATHER_CACHE_TTL
      },

      resolveLocation: async () => {
        const state = get()
        if (state.location) return
        if (state.locationDenied) {
          set({ location: DEFAULT_LOCATION })
          return
        }
        try {
          const loc = await requestGeolocation()
          set({ location: loc, locationDenied: false })
        } catch {
          set({ location: DEFAULT_LOCATION, locationDenied: true })
        }
      },

      fetchWeather: async () => {
        const state = get()
        if (state.isLoading) return

        let location = state.location
        if (!location) {
          await get().resolveLocation()
          location = get().location
        }
        if (!location) return

        set({ isLoading: true, error: null })
        try {
          const data = await fetchWeatherData(location)
          set({ weatherData: data, lastFetchedAt: Date.now(), isLoading: false })
        } catch (err) {
          set({ error: err instanceof Error ? err.message : 'Weather fetch failed', isLoading: false })
        }
      },
    }),
    {
      name: 'weather-data',
      partialize: (state) => ({
        weatherData: state.weatherData,
        location: state.location,
        lastFetchedAt: state.lastFetchedAt,
        locationDenied: state.locationDenied,
      }),
    }
  )
)
